import React from 'react';
import { Container, Header, Grid, PieChart, Box, SpaceBetween, LiveRegion } from '@amzn/awsui-components-core';
import { BarLineChartWithProps } from '../../fintech-ops/fintech-ops-charts/BarChartWithProps';
import { EmptyStateMessage } from '../../fintech-ops/EmptyStateMessage';
import { GRAPH_WIDTHS_BREAK_POINTS } from '../../fintech-ops/FinTechOpsConstants';
import LoadingBar from '@amzn/awsui-chat-components/loading-bar';
import { BarLineChartProps, PieChartData } from './types/interfaces';

type ChartProps = BarLineChartProps | PieChartData;

interface ChartGeneratorProps {
  charts: ChartProps[];
  isLoading: boolean;
  error: string | null;
  showCharts: boolean;
}

export const ChartGenerator: React.FC<ChartGeneratorProps> = ({ charts, isLoading, error, showCharts }) => {
  if (!showCharts) return null;

  if (isLoading) {
    return (
      <Box padding="l">
        <LiveRegion>
          <SpaceBetween direction="vertical" size="s">
            <Box color="text-body-secondary">Generating custom charts...</Box>
            <div className="ai-summary-loading-bar">
              <LoadingBar variant="gen-ai" />
            </div>
          </SpaceBetween>
        </LiveRegion>
      </Box>
    );
  }

  if (error || charts.length === 0) {
    return (
      <Box padding="l">
        <EmptyStateMessage
          title={error || 'No charts available'}
          subtitle={error ? 'Please try a different prompt.' : 'There are no charts to display.'}
        />
      </Box>
    );
  }

  return (
    <SpaceBetween size="s">
      <Grid
        gridDefinition={[
          { colspan: GRAPH_WIDTHS_BREAK_POINTS },
          { colspan: GRAPH_WIDTHS_BREAK_POINTS },
          { colspan: GRAPH_WIDTHS_BREAK_POINTS },
          { colspan: GRAPH_WIDTHS_BREAK_POINTS },
          { colspan: GRAPH_WIDTHS_BREAK_POINTS },
          { colspan: GRAPH_WIDTHS_BREAK_POINTS }
        ]}
      >
        {charts.map((chartProps, index) =>
          'type' in chartProps && chartProps.type === 'pie' ? (
            <Container key={`custom-chart-${index}`} fitHeight={true} header={<Header variant="h2">{chartProps.ariaLabel}</Header>}>
              <PieChart
                {...chartProps}
                empty={<EmptyStateMessage title="No data available" subtitle="There is no data to display for this chart." />}
              />
            </Container>
          ) : (
            <BarLineChartWithProps
              key={`custom-chart-${index}`}
              {...(chartProps as BarLineChartProps)}
              emptyMessage={<EmptyStateMessage title="No data available" subtitle="There is no data to display for this chart." />}
              noMatchMessage="No matching data found"
              errorText="Error loading chart data"
            />
          )
        )}
      </Grid>
    </SpaceBetween>
  );
};
